import React from 'react';
import { Helmet } from 'react-helmet';

interface LocalBusinessData {
  name: string;
  description: string;
  additionalType?: string;
  address: {
    street?: string;
    city: string;
    region: string;
    postalCode?: string;
    country: string;
  };
  geo?: {
    latitude: number;
    longitude: number;
  };
  phone?: string;
  email?: string;
  url: string;
  serviceArea: readonly string[];
  openingHours?: readonly string[];
  priceRange?: string;
  paymentAccepted?: readonly string[];
}

interface LocalSEOStructuredDataProps {
  businessData: LocalBusinessData;
  services?: Array<{
    name: string;
    description: string;
    url: string;
    price?: any;
    areaServed: readonly string[];
  }>;
}

/**
 * Local SEO Structured Data Component
 * Generates rich snippets for local business and services
 */
const LocalSEOStructuredData: React.FC<LocalSEOStructuredDataProps> = ({
  businessData,
  services = []
}) => {
  // Generate LocalBusiness schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": `${businessData.url}/#organization`,
    ...(businessData.additionalType && { "additionalType": businessData.additionalType }),
    "name": businessData.name,
    "description": businessData.description,
    "url": businessData.url,
    "address": {
      "@type": "PostalAddress",
      ...(businessData.address.street && { "streetAddress": businessData.address.street }),
      "addressLocality": businessData.address.city,
      "addressRegion": businessData.address.region,
      ...(businessData.address.postalCode && { "postalCode": businessData.address.postalCode }),
      "addressCountry": businessData.address.country
    },
    ...(businessData.geo && {
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": businessData.geo.latitude,
        "longitude": businessData.geo.longitude
      }
    }),
    ...(businessData.phone && { "telephone": businessData.phone }),
    ...(businessData.email && { "email": businessData.email }),
    "areaServed": businessData.serviceArea.map(area => {
      if (area === "Deutschland") return { "@type": "Country", "name": area };
      if (area === "Bayern" || area === "Mittelfranken") return { "@type": "State", "name": area };
      return { "@type": "City", "name": area };
    }),
    ...(businessData.openingHours && { "openingHours": businessData.openingHours }),
    ...(businessData.priceRange && { "priceRange": businessData.priceRange }),
    ...(businessData.paymentAccepted && { "paymentAccepted": businessData.paymentAccepted })
  };

  // Generate Service schemas
  const serviceSchemas = services.map(service => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "url": service.url,
    "provider": {
      "@type": "LocalBusiness",
      "name": businessData.name,
      "url": businessData.url
    },
    "areaServed": service.areaServed.map(area => {
      if (area === "Deutschland") return { "@type": "Country", "name": area };
      if (area === "Bayern" || area === "Mittelfranken") return { "@type": "State", "name": area };
      return { "@type": "City", "name": area };
    }),
    ...(service.price && {
      "offers": service.price
    })
  }));

  // FAQ structured data removed – provided by page-specific StructuredDataFAQ component to avoid duplicates

  return (
    <Helmet>
      {/* LocalBusiness structured data */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema, null, 2)}
      </script>

      {/* Service structured data */}
      {serviceSchemas.map((schema, index) => (
        <script key={`service-${index}`} type="application/ld+json">
          {JSON.stringify(schema, null, 2)}
        </script>
      ))}

      {/* Local business meta tags */}
      <meta name="geo.region" content={`${businessData.address.country}-${businessData.address.region}`} />
      <meta name="geo.placename" content={businessData.address.city} />
      {businessData.geo && (
        <meta name="geo.position" content={`${businessData.geo.latitude};${businessData.geo.longitude}`} />
      )}
      {businessData.geo && (
        <meta name="ICBM" content={`${businessData.geo.latitude}, ${businessData.geo.longitude}`} />
      )}
    </Helmet>
  );
};

export default LocalSEOStructuredData;